import type { IConversation, IConversationLastMessage } from '~/entities/container/conversation/interfaces/conversation'
import { EDmType } from '~/entities/container/conversation/interfaces/conversation'
import type { CatiaCollabEvent } from '~/entities/container/conversation/interfaces/events'
import type { IUser } from '~/entities/user/user/interfaces/user'
import { useUsersStoreV2 } from '~/entities/user/user/stores/v2-users'

import { AI_NAME, AI_USER, AI_USER_FOR_IS_TYPING } from '~/features-swym/aura/config/ai'
import type { IComment } from '~/features-swym/comment/interfaces/comment'

import { useContentsStore } from '~/features-shared/content/stores/contents'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'
import type {
  RTCConversation,
  RTCFavoriteConversation,
  RTCInternalEvent,
  RTCMessage,
  RTCSection,
  RTCSectionThread,
  RTCUser,
  RTCUserId,
} from '~/features-shared/rtconv/interfaces/rtconversation'
import type { TxID } from '~/features-shared/rtconv/interfaces/rtinterface'
import {
  type IMessageDetails,
  type IMessageDetailsInternalEvent,
  type IParentMessage,
  type ITimeline,
  TimelineStatus,
} from '~/features-shared/timeline/interfaces/timeline'

import type { Section, SectionThread } from '~/features-generic/left-panel/interfaces/left-panel'

/**
 * Builds subject uri for message.
 * @param id - Message id.
 * @param subject_uri - Conversation subject uri.
 * @returns Message subject uri.
 */
function getMessageSubjectUri(id: string, subject_uri: SUBJECT_6W_URI): SUBJECT_6W_URI {
  return `${subject_uri.split(':community:')[0]}:message:rt:${id}`
}

/**
 * Gets a member of a conversation (current or removed member).
 * @param userId - Member id.
 * @returns User object if found.
 */
async function getUser(userId: RTCUserId): Promise<IUser | undefined> {
  const storeUsers = useUsersStoreV2()
  return await storeUsers.getUser(userId)
}

/**
 * Decodes base64 data.
 * @param data Data to parse.
 * @returns Parsed content.
 */
export function parseContent(data: string): string {
  return decodeURIComponent(escape(window.atob(data)))
}

/* eslint-disable max-statements */
/* eslint-disable complexity */
/* eslint-disable max-depth */
/* eslint-disable max-nested-callbacks */

/**
 * Transforms a RTCUser into an IUser.
 * @param data - Raw RTC data
 * @returns The RTCUser transformed into an IUser.
 */
export function toIUser(data: RTCUser): IUser {
  const user: IUser = {
    uid: data.uuid === AI_USER ? AI_USER_FOR_IS_TYPING : data.uuid,
    id: data.id.toString(),
    first_name: data.uuid === AI_USER ? AI_NAME : data.firstName,
    last_name: data.uuid === AI_USER ? '' : data.lastName,
    login: data.uuid === AI_USER ? AI_USER_FOR_IS_TYPING : data.login,
    displayName: data.uuid === AI_USER ? AI_NAME : `${data.firstName} ${data.lastName}`,

    email: data.email,
    tel: data.phone,
    country: data.country,
    city: data.city,
  }

  // store users
  const storeUsers = useUsersStoreV2()
  storeUsers.setUser(data.id, user)

  return user
}

/**
 * Transforms a RTCConversation into an IConversation.
 * @param data - Raw RTC conversation object.
 * @returns The RTCConversation transformed into an IConversation.
 */
export function toIConversation(data: RTCConversation): IConversation {
  const lastMessage: IConversationLastMessage | undefined = (() => {
    // First load, get last message from all unread conversation. RT event 'getConversations'
    if (data.lastMsg)
      return { message_id: data.lastMsg.id.toString() }
    // Get last message by real time chat. RT event 'newMessages'
    else if (data.lastMsgId)
      return { message_id: data.lastMsgId.toString() }
    else
      return undefined
  })()

  const conversation: IConversation = {
    community_type: 'dm',
    id: data.id.toString(),
    subject_uri: data.subjectUri,
    created_at: data.dateCreation,
    updated_at: data.dateLastMsgCreate,

    is_hidden: data.isHidden,
    is_muted: data.isMuted,
    topic: data.title ?? null,
    dm_type: data.isPrivate ? EDmType.Private : EDmType.Group,
    users: data.members.map(toIUser),
    last_message: lastMessage,
    mark: data.lastMsgReadId ? { message_id: data.lastMsgReadId.toString() } : undefined,
    is_persistent: !data.automaticRemoval,
  }

  conversation.removedUsers = data.removedMembers?.map(toIUser) ?? []

  return conversation
}

/**
 * Transform RTCSection to Sections
 * @param data RTCSection
 * @returns The RTCSection transformed into an Section<SectionThread>.
 */
export function transformRTCSectionToSection(data: RTCSection): Section<SectionThread> {
  return {
    id: data.id.toString(),
    name: data.name,
    position: data.readOnly ? data.readOnlyPosition : data.position,
    isFavorite: data.readOnly && data.readOnlyPosition === 1,
    isReadOnly: data.readOnly || false,
    threads: data.conversations.map((conv) => {
      return {
        threadId: conv.convId.toString(),
        position: conv.position,
      }
    }),
  }
}
/**
 * Transform RTC favorite conversation to Conversation
 * @param data RTCFavoriteConversation
 * @returns The RTCFavoriteConversation transformed into an RTCSectionThread.
 */
export function transformRTCFavoriteToConversation(data: RTCFavoriteConversation): RTCSectionThread {
  return {
    convId: data.id,
    position: data.favorite,
  }
}

/**
 * Transforms a RTCMessage into a IParentMessage.
 * @param data - Raw RTC message object.
 * @param conversationUsers
 * @returns The RTCMessage transformed into an IParentMessage.
 */
function toIParentMessage(data: RTCMessage, conversationUsers: IUser[]): IParentMessage | undefined {
  const parentAuthor = conversationUsers.find(user => user.id === data.author.toString())
  if (!parentAuthor)
    return

  const parentContent: string = data.contents[0].data // always a text message

  const parentMessage: IParentMessage = {
    author: parentAuthor,
    message_details: {
      content: parentContent,
    },
    subject_uri: data.subjectUri,
    message_id: data.id.toString(),
    creation_date: data.creationDate,
  }

  return parentMessage
}

/**
 * Transforms a RTCInternalEvent into an IMessageDetailsInternalEvent.
 * @param internalEvent - Raw RTC internal event object.
 * @param conversation - Conversation where the internal event has been published.
 * @param modification_date - Modification date of parent ITimeline.
 * @returns The IComment transformed into an IMessageDetailsInternalEvent.
 */
async function toIMessageDetailsInternalEvent(
  internalEvent: RTCInternalEvent,
  conversation: IConversation,
  modification_date: string,
): Promise<IMessageDetailsInternalEvent> {
  const messageDetailsInternalEvent: IMessageDetailsInternalEvent = {
    model_content: '',
    model_state: 'ACTIVE',
    moderation_state: 'notModerated',
    commentable_uri: '',
    model_id: '',
    model_uri: '',
    subject_title: internalEvent.eventData[0]?.subject_title ?? conversation.topic,
    subject_model: internalEvent.eventData[0]?.subject_model ?? 'dm',
    subject_id: internalEvent.eventData[0]?.subject_id ?? conversation.id,

    model: internalEvent.eventData[0]?.type ?? 'Externaldm',
    action: internalEvent.eventType,

    modification_date,
  }

  switch (internalEvent.eventType) {
    case 'convCreated':
      break // no additional data needed for nls
    case 'titleUpdated':
      if (internalEvent.eventData)
        messageDetailsInternalEvent.topic = internalEvent.eventData[0]
      break
    case 'usersAdded':
      messageDetailsInternalEvent.users = await Promise.all(internalEvent.eventData.map(async userId => await getUser(userId)))
      break
    case 'userRemoved':
      messageDetailsInternalEvent.users = await Promise.all(internalEvent.eventData.map(async userId => await getUser(userId)))
      break
    case 'updateAutoRemoval':
      messageDetailsInternalEvent.autoRemovalEnabled = internalEvent.eventData[0]
      break
    case 'msgDeleted':
      messageDetailsInternalEvent.user = await getUser(internalEvent.eventData)
      break
    case 'changeStatus':
      messageDetailsInternalEvent.wedo_maturity = internalEvent.eventData[1]
      break
    case 'startedCall':
      messageDetailsInternalEvent.users = await Promise.all(internalEvent.eventData.users.map(async userId => await getUser(userId)))
      break
    case 'contentEdited':
      break // no additional data needed for nls
    case 'catiaCollab':
      messageDetailsInternalEvent.action = internalEvent.eventData[0].serviceId
      messageDetailsInternalEvent.model_id = internalEvent.eventData[0].msgId
      messageDetailsInternalEvent.subject_title = internalEvent.eventData[0].additionalInfo
      break
    default:
    // unsupported event, nothing to do
  }

  return messageDetailsInternalEvent
}

/**
 * Transforms an IComment into a IMessageDetailsInternalEvent.
 * @param comment - Comment retrieved from 3DComment.
 * @param contentUri - content_uri of the parent content of the comment (ex: for a comment post it will be the post content_uri & for a meeting topic comment it will be the meeting content_uri and not the topic one )
 * @returns The IComment transformed into an IMessageDetailsInternalEvent.
 */
async function toIMessageDetailsComment(
  comment: IComment,
  contentUri?: string,
): Promise<IMessageDetailsInternalEvent | undefined> {
  const storeContents = useContentsStore()
  const content = await storeContents.fetchContent(contentUri || comment.content_uri, true)

  if (!content)
    return

  const messageDetailsComment: IMessageDetailsInternalEvent = {
    model_content: comment.description,
    model_state: comment.access_state,
    moderation_state: comment.moderation_state,
    commentable_uri: comment.content_uri,
    action: 'create',
    model: 'Comment',
    model_id: comment.subject_uri.split(':')
      .pop() || comment.subject_uri,
    model_uri: comment.subject_uri,
    parent_uri: comment.parent_comment_uri ?? undefined,
    subject_title: content.title,
    subject_model: content.model,
    subject_id: content.subject_uri.split(':')
      .pop() || content.subject_uri,
    modification_date: comment.modified_at,
  }

  return messageDetailsComment
}

/**
 * Transforms a RTCMessage into aN ITimeline.
 * @param data - Raw RTC message object.
 * @param conversation - Raw RTC conversation object.
 * @param TxID - Transaction id.
 * @param listCommentsUrisPromises - Array of promises of listCommentsUris calls.
 * @returns The RTCMessage transformed into an ITimeline.
 */
export async function toITimeline(
  data: RTCMessage,
  conversation: IConversation,
  TxID: TxID,
  listCommentsUrisPromises?: Promise<IComment[]>[],
): Promise<ITimeline | undefined> {
  const storeContents = useContentsStore()

  const author = await getUser(data.author)
  if (!author)
    throw new Error(`[newMessage] Author of message with id ${data.id} not found in conversation members`)

  // 1. Create the ITimeline object.
  const timeline: Partial<ITimeline> = {
    'status': TimelineStatus.SENT,
    'id': data.id.toString(),
    'subject_uri': getMessageSubjectUri(data.id.toString(), conversation.subject_uri),
    'created_at': data.creationDate,
    'updated_at': data.editionDate ?? data.creationDate,

    'thread_id': conversation.id,
    'modification_date': data.editionDate,
    'community_type': 'dm',
    'community_id': conversation.subject_uri,
    'community_subject_uri': conversation.subject_uri,
    'message_uri': data.id.toString(),
    'subject_state': 'ACTIVE',
    'external_app_id': 'RTC',
    author,
    'from_index': false,
    // triggered_by,
    // currentUserEndorse, // done in getMessages listener
    'X-Request-ID': TxID,
  }

  // 2. Fill the message_details.
  // Now that we can have multiple textContent value un data.contents array, we need to remove all textContent to have the first element who will be the one was used before
  const contentData = data.contents.filter(e => e.type !== 'textContent')[0]
  /**
   * All textContent of the message and decode it if it exist
   */
  const textContentData = data.contents.filter(e => e.type === 'textContent')
    ?.map(el => ({ ...el, data: JSON.parse(atob(el.data)) }))

  if (contentData.type === 'internal') {
    // 2-a. First, format the internal message.
    const internalEventData = parseContent(contentData.data)
    timeline.message_type = 'INTERNAL_EVENT'
    const internalEvent: RTCInternalEvent = JSON.parse(internalEventData)
    const messageDetailsInternalEvent = await toIMessageDetailsInternalEvent(
      internalEvent,
      conversation,
      data.editionDate ?? data.creationDate,
    )
    if (messageDetailsInternalEvent) {
      timeline.message_details = messageDetailsInternalEvent
      if (internalEvent.eventData[0]?.subject_id) {
        timeline.subject_uri = internalEvent.eventData[0].subject_id
        if (internalEvent.eventType === 'contentEdited') {
          storeContents.fetchContent(internalEvent.eventData[0]?.subject_id, true)
            .then((content) => {
              if (content)
                content.last_publisher = author
            })
        }
      }
    }
    else { return } // if event is not supported.
  }
  else if (contentData.type === 'text') {
    // 2-b. First, format the text message.
    timeline.message_type = 'INSTANT_MESSAGE'

    const message_details: IMessageDetails = {
      content: contentData.data,
      textContent: textContentData.length ? textContentData : undefined,
      moderation_state: data.editionDate ? 'moderatedByAuthor' : 'notModerated',
      parent_message: data.parentMessage ? toIParentMessage(data.parentMessage, conversation.users) : undefined,
      forwarded_from: data.forwardedFrom ? toIUser(data.forwardedFrom) : null,
    }
    timeline.message_details = message_details
  }
  else if (contentData.type === 'comment') {
    // 2-c. First, wait for comment to be fetched.
    // Comments are fetched in batch via listCommentsUris called in getMessages listener.
    if (listCommentsUrisPromises) { // use a dedicated if so that if the promises are missing, it does not go into the else statements
      const subject_uri = parseContent(contentData.data)
      timeline.message_type = 'INTERNAL_EVENT'

      const results = await Promise.allSettled(listCommentsUrisPromises?.map(listCommentsUrisPromise =>
        new Promise<IComment>((resolve, reject) => {
          listCommentsUrisPromise.then((comments) => {
            // We need to find the correct comment in the list of retrieved comments.
            const comment = comments?.find(c => c.subject_uri === subject_uri)
            if (comment)
              resolve(comment)
            else reject(new Error(`Failed to find ${subject_uri} in comment response`))
          })
            .catch(() => {
              // Catch the possible error of the promise to not block the other timelines creation
              reject(new Error(`Failed to retrieve comment's of ${subject_uri} in conversation ${conversation.subject_uri}`))
            })
        })))

      for (const result of results) {
        if (result.status === 'fulfilled') {
          const messageDetailsComment = await toIMessageDetailsComment(result.value, contentData?.metadata?.subjectUri)
          if (!messageDetailsComment)
            return
          timeline.message_details = messageDetailsComment
          timeline.subject_uri = messageDetailsComment.commentable_uri
          break
        }
      }
      if (!timeline.message_details)
        return // if all listCommentsUrisPromises rejected
    }
  }
  else if (contentData.type === 'transient') {
    timeline.message_type = 'INTERNAL_EVENT'
    const internalEvent: RTCInternalEvent = JSON.parse(contentData.data)
    const messageDetailsInternalEvent = await toIMessageDetailsInternalEvent(
      internalEvent,
      conversation,
      data.editionDate ?? data.creationDate,
    )
    if (messageDetailsInternalEvent)
      timeline.message_details = messageDetailsInternalEvent
  }
  else {
    // 2-d. First, fetch the content from 3DSwym. For media, only fetch metadata.
    const subject_uri = parseContent(contentData.data)
    timeline.message_type = 'INTERNAL_CONTENT'
    const content = await storeContents.fetchContent(subject_uri, true)
    const title = content?.title
    const model = content?.model ?? ''
    const media_type = content?.media_type
    const preview_media_file_ext = content?.preview_media_file_ext
    // .catch((error) => ()) todo check if catch here, catch in getPost removed
    if (!content)
      return // if fetch failed.
    timeline.message_details = { subject_uri, title, model, type: media_type, preview_media_file_ext }
    timeline.subject_uri = subject_uri
  }

  return timeline as ITimeline
}

/**
 * Transforms raw data for transient messages to ITimeline object
 *
 * @param data The parameters
 * @param data.userId The id of the user
 * @param data.date The date timestamp
 * @param data.messageId The id of the message
 * @param data.convId The id of the conversation
 * @param data.serviceId The service id of the event
 * @param data.conversation The conversation object
 * @param data.TxID The id of the transaction
 * @param data.message Optional encoded data
 * @param data.parser A custom parser to parse the decoded message. Return string if valid, undefined if invalid
 * @returns the ITimeline object
 */
export async function transientToITimeline(
  data: {
    userId: RTCUserId
    date: number
    messageId: string
    convId: string
    serviceId: CatiaCollabEvent
    conversation: IConversation
    TxID: string
    message?: string
    parser?: (data: string) => Promise<string | undefined>
    uri?: boolean
  },
) {
  let message = ''
  if (data.message && data.parser) {
    const decoded = data.uri === false ? window.atob(data.message) : parseContent(data.message)
    const parsedMessage = await data.parser(decoded)

    // If the parsed message is undefined, then timeline item is not valid
    if (parsedMessage == null)
      return

    message = parsedMessage
  }

  return toITimeline(
    {
      id: data.date,
      author: data.userId,
      conv: +data.convId,
      creationDate: new Date(data.date)
        .toISOString(),
      likes: [],
      contents: [{
        id: data.date,
        subjectUri: data.messageId,
        type: 'transient', // This message disappears on refresh
        data: JSON.stringify({
          eventType: 'catiaCollab',
          eventData: [{
            serviceId: data.serviceId,
            msgId: data.messageId,
            additionalInfo: message,
          }],
        }),
      }],
    },
    data.conversation,
    data.TxID,
    [],
  )
}
